import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Contact, FileText, Hash, Headphones, Lock, MessageSquareText, Pencil, Send, Copy, Mail } from "lucide-react";
import { toast } from "sonner";
import { Avatar, Presence } from "@/components/Avatar";
import { MessageItem } from "@/components/MessageItem";
import { FileIcon } from "@/components/Attachments";
import { useChat } from "@/lib/chat";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { copyText, fmtFull, fmtRelative, pluralize } from "@/lib/format";
import { fmtBytes } from "@/lib/upload";
import { analytics } from "@/lib/analytics";

const useCtx = () => {
  const { usersByHandle, usersById, meId, openProfile } = useChat();
  return useMemo(() => ({ usersByHandle, usersById, meId, onMentionClick: (u) => openProfile(u.id) }), [usersByHandle, usersById, meId, openProfile]);
};

export const Header = ({ icon: Icon, title, sub, children }) => (
  <div className="tc-view__header" data-testid="view-header"><Icon size={18} /> {title} {sub && <span className="sub">{sub}</span>}<span style={{ marginLeft: "auto" }}>{children}</span></div>
);

const ConvLink = ({ c, onClick }) => (
  <span className="link" onClick={onClick}>{c?.type === "channel" ? <>{c.is_private ? <Lock size={12} style={{ display: "inline" }} /> : "#"} {c.name}</> : c?.display_name}</span>
);

export const ThreadsView = () => {
  const { openThread, openConversation } = useChat();
  const [list, setList] = useState(null);
  const ctx = useCtx();
  useEffect(() => { api.get("/threads").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  return (
    <div className="tc-view" data-testid="threads-view">
      <Header icon={MessageSquareText} title="Threads" />
      <div className="tc-view__body tc-scroll">
        {list && !list.length && <div className="tc-empty"><MessageSquareText /><h3>Threads you're part of will show up here</h3><p>Replies to messages you've posted or participated in are collected here so you never lose the plot.</p></div>}
        {(list || []).map((p) => (
          <div className="tc-card" key={p.id} data-testid={`thread-card-${p.id}`}>
            <div className="tc-card__head"><ConvLink c={p.conversation} onClick={() => openConversation(p.conversation_id)} /><span className="when">{fmtRelative(p.last_reply_at)}</span></div>
            <MessageItem m={p} ctx={ctx} hideThreadIndicator />
            {p.latest_replies.map((r) => <MessageItem key={r.id} m={r} ctx={ctx} inThread />)}
            <div className="tc-card__foot"><button className="tc-card__replylink" onClick={() => { openConversation(p.conversation_id); openThread(p.id, p.conversation_id); }} data-testid={`thread-card-open-${p.id}`}>{p.reply_count > 3 ? `View all ${p.reply_count} replies` : "Reply in thread"}</button></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const HuddlesView = () => {
  const { huddles, joinHuddle, myHuddleId, conversations, openConversation } = useChat();
  const [history, setHistory] = useState([]);
  useEffect(() => { api.get("/huddles/history").then((r) => setHistory(r.data)).catch(() => {}); }, [huddles.length]);
  return (
    <div className="tc-view" data-testid="huddles-view">
      <Header icon={Headphones} title="Huddles" sub="Live audio with your team" />
      <div className="tc-view__body tc-scroll">
        <h3 style={{ fontSize: 15, fontWeight: 900, margin: "0 0 10px" }}>Live now</h3>
        {!huddles.length && <div className="tc-card" style={{ padding: 16, color: "var(--tc-text-muted)", fontSize: 14 }}>No huddles are happening right now. Start one from any channel header with the headphones button.</div>}
        {huddles.map((h) => (
          <div className="tc-card" key={h.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 12 }} data-testid={`live-huddle-${h.id}`}>
            <span style={{ display: "inline-flex" }}>{h.participants_info.slice(0, 5).map((u) => <Avatar key={u.id} user={u} size={32} radius={8} style={{ marginLeft: -6, boxShadow: "0 0 0 2px var(--tc-bg)" }} />)}</span>
            <div style={{ flex: 1 }}><b>{h.conversation?.type === "channel" ? `#${h.conversation.name}` : h.conversation?.display_name}</b><div style={{ fontSize: 13, color: "var(--tc-text-muted)" }}>{pluralize(h.participants.length, "participant")} · started {fmtRelative(h.started_at)}</div></div>
            <button className="tc-btn tc-btn--primary sm" onClick={() => (h.id === myHuddleId ? openConversation(h.conversation_id) : joinHuddle(h.conversation_id))} data-testid={`join-huddle-${h.id}`}><Headphones size={14} /> {h.id === myHuddleId ? "Open" : "Join"}</button>
          </div>
        ))}
        <h3 style={{ fontSize: 15, fontWeight: 900, margin: "24px 0 10px" }}>Start a huddle</h3>
        <div className="tc-dir-grid">
          {conversations.filter((c) => c.type === "channel").slice(0, 8).map((c) => (
            <button key={c.id} className="tc-dir-card" onClick={() => joinHuddle(c.id)} data-testid={`start-huddle-${c.name}`}><Headphones size={28} style={{ color: "var(--tc-green)" }} /><span className="name"># {c.name}</span><span className="title">{c.member_count} members</span></button>
          ))}
        </div>
        {history.length > 0 && (
          <>
            <h3 style={{ fontSize: 15, fontWeight: 900, margin: "24px 0 10px" }}>Recent</h3>
            <div style={{ overflowX: "auto" }}><table className="tc-table"><thead><tr><th>Conversation</th><th>Started by</th><th>When</th><th>Duration</th><th className="num">People</th><th>Status</th></tr></thead>
              <tbody>{history.map((h) => <tr key={h.id} data-testid={`huddle-history-${h.id}`}><td style={{ whiteSpace: "nowrap" }}>{h.conversation?.type === "channel" ? `#${h.conversation.name}` : h.conversation?.display_name}</td><td style={{ whiteSpace: "nowrap" }}>{h.started_by_info?.display_name || "—"}</td><td style={{ whiteSpace: "nowrap" }}>{fmtFull(h.started_at)}</td><td>{h.duration_sec == null ? "—" : h.duration_sec < 60 ? `${h.duration_sec}s` : `${Math.round(h.duration_sec / 60)} min`}</td><td className="num">{h.people}</td><td>{h.active ? <span className="tc-pill server">Live</span> : <span className="tc-pill">Ended</span>}</td></tr>)}</tbody></table></div>
          </>
        )}
      </div>
    </div>
  );
};

export const DraftsView = () => {
  const { openConversation } = useChat();
  const [drafts, setDrafts] = useState([]);
  useEffect(() => { api.get("/drafts").then((r) => setDrafts(r.data)).catch(() => {}); }, []);
  return (
    <div className="tc-view" data-testid="drafts-view">
      <Header icon={Send} title="Drafts & sent" />
      <div className="tc-view__body tc-scroll">
        {!drafts.length && <div className="tc-empty"><Pencil /><h3>No drafts yet</h3><p>Start typing in any conversation and we'll keep an unsent draft here for you.</p></div>}
        {drafts.map((d) => (
          <button key={`${d.conversation.id}-${d.parent_id}`} className="tc-card" style={{ width: "100%", textAlign: "left", padding: 12 }} onClick={() => openConversation(d.conversation.id)} data-testid={`draft-${d.conversation.id}`}>
            <div style={{ display: "flex", gap: 8, fontSize: 13, color: "var(--tc-text-muted)", marginBottom: 4 }}><Pencil size={13} /> Draft to <b style={{ color: "var(--tc-text)" }}>{d.conversation.type === "channel" ? `#${d.conversation.name}` : d.conversation.display_name}</b>{d.parent_id && " (thread)"}<span style={{ marginLeft: "auto" }}>{fmtRelative(d.updated_at)}</span></div>
            <div style={{ fontSize: 15 }}>{d.text}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const DirectoryView = () => {
  const { users, openProfile, openDm, setModal } = useChat();
  const { workspace } = useAuth();
  const [tab, setTab] = useState("members");
  const [channels, setChannels] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { if (tab === "channels") api.get("/conversations/browse").then((r) => setChannels(r.data)).catch(() => {}); }, [tab]);
  const people = users.filter((u) => !u.is_system_bot && (!q || `${u.display_name} ${u.handle} ${u.title} ${u.chat_email}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="tc-view" data-testid="directory-view">
      <Header icon={Contact} title="Directories" sub={workspace?.name} />
      <div className="tc-view__filterbar">
        <div className="tc-tabsbar" style={{ margin: 0, borderBottom: 0 }}><button className={tab === "members" ? "is-active" : ""} onClick={() => setTab("members")} data-testid="directory-tab-members">Members ({users.filter((u) => !u.is_bot).length})</button><button className={tab === "channels" ? "is-active" : ""} onClick={() => setTab("channels")} data-testid="directory-tab-channels">Channels</button></div>
        <input className="tc-input xs" style={{ maxWidth: 280, marginLeft: "auto" }} placeholder={tab === "members" ? "Search members" : "Search channels"} value={q} onChange={(e) => setQ(e.target.value)} data-testid="directory-search" />
      </div>
      <div className="tc-view__body tc-scroll">
        {tab === "members" ? (
          <div className="tc-dir-grid">
            {people.map((u) => (
              <div key={u.id} className="tc-dir-card" data-testid={`directory-member-${u.handle}`}>
                <button onClick={() => openProfile(u.id)}><Avatar user={u} size={80} radius={12} /></button>
                <span className="name">{u.display_name}<Presence presence={u.presence} /></span>
                <span className="title">{u.title || `@${u.handle}`}</span>
                <span className="email" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{u.chat_email}<button className="tc-copybtn" title="Copy email" onClick={() => { copyText(u.chat_email); analytics.track("email_copied", { category: "email", target: "user", target_id: u.id, label: u.chat_email }); toast.success("Email copied"); }}><Copy /></button></span>
                <button className="tc-btn tc-btn--outline sm" style={{ marginTop: 6 }} onClick={() => openDm([u.id])} data-testid={`directory-message-${u.handle}`}>Message</button>
              </div>
            ))}
          </div>
        ) : (
          <div className="tc-detail-block" style={{ marginTop: 0 }}>
            {channels.filter((c) => !q || c.name.includes(q.toLowerCase())).map((c) => (
              <div key={c.id} className="tc-channel-row" data-testid={`directory-channel-${c.name}`}>
                <div className="info"><div className="name">{c.is_private ? <Lock size={14} /> : <Hash size={15} />} {c.name}</div><div className="desc">{c.member_count} members · {c.description || c.topic || "No description"} · <Mail size={11} style={{ display: "inline" }} /> {c.email}</div></div>
                <button className="tc-btn tc-btn--outline sm" onClick={() => setModal({ type: "details", conversationId: c.id })}>Details</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const ToolsView = () => {
  const { users, openDm, me } = useChat();
  const { workspace } = useAuth();
  const navigate = useNavigate();
  const bot = users.find((u) => u.is_system_bot);
  return (
    <div className="tc-view" data-testid="tools-view">
      <Header icon={Bot} title="Agents & tools" />
      <div className="tc-view__body tc-scroll">
        <div className="tc-card" style={{ padding: 20, display: "flex", gap: 16, alignItems: "flex-start" }}>
          <Avatar user={bot || { display_name: "Total Chat" }} size={64} radius={12} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>Total Chat <span className="tc-msg__app">App</span></h3>
            <p style={{ color: "var(--tc-text-muted)", margin: "4px 0 12px" }}>Posts inbound emails, API messages and automated alerts into your channels. Every channel and member has an address on <b>{workspace?.email_domain}</b>.</p>
            <div style={{ display: "flex", gap: 8 }}>
              {bot && <button className="tc-btn tc-btn--outline sm" onClick={() => openDm([bot.id])} data-testid="tools-message-bot">Message</button>}
              {me?.is_admin && <button className="tc-btn tc-btn--primary sm" onClick={() => navigate("/admin/api-keys")} data-testid="tools-manage-api">Manage API keys</button>}
              {me?.is_admin && <button className="tc-btn tc-btn--outline sm" onClick={() => navigate("/admin/api-docs")} data-testid="tools-api-docs">API docs</button>}
            </div>
          </div>
        </div>
        <div className="tc-card" style={{ padding: 20 }}>
          <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 900 }}>Connect other tools</h3>
          <p style={{ color: "var(--tc-text-muted)", fontSize: 14, margin: 0 }}>Reporting tools, CRMs and automation platforms can pull granular activity data (every click, message, reaction, huddle and login) through the REST API with an API key, or push messages and emails into Total Chat.</p>
        </div>
      </div>
    </div>
  );
};

export const FilesList = ({ onOpen }) => {
  const [items, setItems] = useState(null);
  const [files, setFiles] = useState(null);
  const [tab, setTab] = useState("files");
  useEffect(() => {
    api.get("/links").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/files").then((r) => setFiles(r.data)).catch(() => setFiles([]));
  }, []);
  const convLabel = (c) => (c?.type === "channel" ? `#${c.name}` : c?.display_name);
  return (
    <aside className="tc-sidebar" data-testid="files-list">
      <div className="tc-sidebar__header"><span className="tc-sidebar__ws" style={{ cursor: "default" }}><span>Files</span></span></div>
      <div className="tc-tabsbar" style={{ margin: "0 12px 8px" }}>
        <button className={tab === "files" ? "is-active" : ""} onClick={() => setTab("files")} data-testid="files-tab-uploads">Files{files?.length ? ` (${files.length})` : ""}</button>
        <button className={tab === "links" ? "is-active" : ""} onClick={() => setTab("links")} data-testid="files-tab-links">Links{items?.length ? ` (${items.length})` : ""}</button>
      </div>
      <div className="tc-sidebar__list tc-scroll" style={{ padding: "0 8px" }}>
        {tab === "files" && files && !files.length && <div style={{ padding: 16, fontSize: 13, color: "var(--tc-text-muted)" }}>Files shared in your conversations will show up here. Drag a file into any conversation to upload it.</div>}
        {tab === "files" && (files || []).map((f) => (
          <button key={f.id} className="tc-card tc-file-row" onClick={() => onOpen({ conversation_id: f.conversation_id, id: f.message_id, parent_id: null })} data-testid={`file-item-${f.id}`}>
            <span className="tc-file-row__icon"><FileIcon kind={f.kind} size={20} /></span>
            <span className="tc-file-row__info">
              <span className="tc-file-row__name">{f.name}</span>
              <span className="tc-file-row__meta">{convLabel(f.conversation)} · {f.user?.display_name} · {fmtBytes(f.size)} · {fmtRelative(f.created_at)}</span>
            </span>
          </button>
        ))}
        {tab === "links" && items && !items.length && <div style={{ padding: 16, fontSize: 13, color: "var(--tc-text-muted)" }}>Links shared in your conversations will show up here.</div>}
        {tab === "links" && (items || []).map((m) => (
          <button key={m.id} className="tc-card" style={{ width: "100%", textAlign: "left", padding: 10, background: "var(--tc-bg)" }} onClick={() => onOpen(m)} data-testid={`link-item-${m.id}`}>
            <div style={{ fontSize: 12, color: "var(--tc-text-muted)", display: "flex", gap: 6 }}><FileText size={12} /> {convLabel(m.conversation)} · {m.user?.display_name}</div>
            {m.links.slice(0, 2).map((l) => <div key={l} style={{ fontSize: 13, color: "var(--tc-link)", wordBreak: "break-all" }}>{l}</div>)}
          </button>
        ))}
      </div>
    </aside>
  );
};
